import { stegaClean } from '@sanity/client/stega';
import type { HydrogenCart } from '@shopify/hydrogen';
import type { GetCustomerQuery } from 'customerapi.generated';
import type { Logger } from '~/lib/logger/logger.interface';
import type { AuthVisibility } from '~/types/sanity-typegen';

export const isValidCustomer = ({ customer }: { customer: GetCustomerQuery['customer'] }) => {
  // Check whether the customer is assigned to a company
  if (!customer?.companyContacts?.edges?.length) {
    return false;
  }

  return true;
};

export const withBearer = (token: string | null) => (token ? `Bearer ${token}` : '');

/**
 * Initialise an empty cart (primarily used for users with invalid accounts)
 *
 * This is needed for scenarios where a user switches from a valid to an invalid account, e.g:
 * 1. User with valid account adds items to cart
 * 2. User logs out and switches to an invalid account
 * 3. Items from previous session persist in cart
 *
 * Expected behavior:
 * - Valid account: Restore an existing cart if present in cookies
 * - Invalid account: Always initalise an empty cart to prevent user from checking out with items from previous session
 */
export const clearCart = async (cart: HydrogenCart, logger?: Logger) => {
  // Get current cart info before clearing for logging
  try {
    const currentCart = await cart.get();
    const itemCount = currentCart?.totalQuantity ?? 0;

    if (itemCount > 0 && logger) {
      logger.warn('Cart cleared', {
        cartId: currentCart?.id,
        itemCount,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {
    // Log error but continue with cart creation
    if (logger) {
      logger.error('Error getting cart info before clearing', { error });
    }
  }

  return cart.create({});
};

/**
 * Determines visiblity based on the Sanity auth setting and the user's authentication status.
 *
 * @param authVisibility - the auth visibility setting from Sanity
 * @param isAuthenticated - whether the user is currently authenticated
 * @returns `true` if should be shown, `false` if should be hidden
 */
export const checkAuthVisibility = (authVisibility: AuthVisibility, isAuthenticated: boolean) => {
  const cleanedAuthVisibility = stegaClean(authVisibility);
  if (
    (!isAuthenticated && cleanedAuthVisibility === 'loggedIn') ||
    (isAuthenticated && cleanedAuthVisibility === 'loggedOut')
  ) {
    return false;
  }

  return true;
};
